import { View, Text, Pressable, StyleSheet, ScrollView } from "react-native";
import Icon, { IconType } from "@/ui/Icon";
import { Colors } from "@/constants/Colors";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { useRouter } from "@/_router";
import React, { useEffect } from "react";
import { getBasePath } from "@/models/utils";
import { useUISlice } from "@/models/useUISlice";
import WebPageFooter from "./WebPageFooter";
import { useUserSlice } from "@/models/useUserSlice";
import Menu, { MenuAction } from "@/ui/Menu";

type Tab = "home" | "learn" | "settings";
type Screen = "note" | "home" | "settings";

export default function WebPage({
  children,
  tab,
  screenHeader,
  bgColor,
}: {
  children: React.ReactNode;
  tab: Tab;
  bgColor?: string;
  screenHeader?: {
    right?: {
      menu?: MenuAction[];
      icon?: IconType;
    };
    onBack?: () => void;
    left?: {
      onPress?: () => void;
    };
  };
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const height = useUISlice((state) => state.device.height);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const insets = useUISlice((state) => state.device.safeArea);
  const currentScreen = router.pathname.slice(1) as Screen;

  useEffect(() => {
    useUserSlice.getState().subscription().onStripeCheckoutSuccess({ router });
  }, []);

  const _gotoHome = () => {
    router.push(`${getBasePath()}/home`);
  };

  const _gotoSettings = () => {
    router.push(`${getBasePath()}/settings`);
  };

  const _gotoUpgrade = () => {
    useUISlice.getState().setPaywallVisible(true);
  };

  if (isMobile) {
    return (
      <View
        style={{
          backgroundColor: bgColor || Colors.bg,
          width: "100%",
          height: "100%",
          paddingHorizontal: ms(16),
          top: 0,
          paddingTop: insets.top,
          left: 0,
          ...({ position: "fixed" } as any),
        }}
      >
        {/* Header */}
        {currentScreen === "home" && (
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              height: ms(60),
            }}
          >
            <Text onPress={_gotoHome} style={styles.logo}>
              🍋 EZNOTE <Text style={styles.logoAI}>AI</Text>
            </Text>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ms(12),
              }}
            >
              <Pressable
                onPress={_gotoSettings}
                style={{
                  width: ms(32),
                  height: ms(32),
                  borderRadius: ms(24),
                  backgroundColor: Colors.text + "10",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="cog"
                  style={{
                    tintColor: Colors.accent,
                    width: ms(16),
                    height: ms(16),
                  }}
                />
              </Pressable>
            </View>
          </View>
        )}

        {["note", "settings"].includes(currentScreen) && (
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingVertical: ms(4),
            }}
          >
            <Pressable
              style={{
                width: ms(32),
                height: ms(32),
                marginLeft: -ms(4),
                alignItems: "center",
                justifyContent: "center",
                borderRadius: ms(24),
                backgroundColor: Colors.text + "10",
              }}
              onPress={
                screenHeader?.onBack ? screenHeader.onBack : () => router.back()
              }
            >
              <Icon
                icon="caretRight"
                style={{
                  width: ms(20),
                  height: ms(20),
                  tintColor: Colors.text,
                  transform: [{ rotate: "180deg" }],
                }}
              />
            </Pressable>

            <View
              style={{
                flexDirection: "row",
                gap: ms(12),
                alignItems: "center",
                height: ms(48),
              }}
            >
              {/* <Pressable
                style={{
                  height: ms(32),
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: ms(16),
                  borderRadius: ms(20),
                  backgroundColor: Colors.text + "10",
                  flexDirection: "row",
                  gap: ms(4),
                }}
                onPress={() => null}
              >
                <Text
                  style={{
                    color: Colors.accent,
                    fontSize: ms(12),
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {t("webPage.share")}
                </Text>
              </Pressable> */}

              {screenHeader?.right?.menu && (
                <Menu
                  key={JSON.stringify(screenHeader.right.menu)}
                  actions={screenHeader.right.menu}
                  actionComponent={
                    <View
                      style={{
                        backgroundColor: Colors.text + "10",
                        height: ms(32),
                        width: ms(32),
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: ms(24),
                      }}
                    >
                      <Icon
                        icon={screenHeader.right.icon}
                        style={{
                          width: ms(20),
                          height: ms(20),
                          tintColor: Colors.accent,
                        }}
                      />
                    </View>
                  }
                />
              )}
            </View>
          </View>
        )}

        <ScrollView
          style={{
            marginHorizontal: -ms(16),
            paddingHorizontal: ms(16),
            flexGrow: 1,
          }}
        >
          {children}
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={{ ...styles.header, position: "fixed" } as any}>
        <Text onPress={_gotoHome} style={styles.logo}>
          🍋 EZNOTE <Text style={styles.logoAI}>AI</Text>
        </Text>

        {!isSubscribed && !screenHeader?.right?.menu && (
          <Pressable onPress={_gotoUpgrade} style={styles.upgradeButton}>
            <Text style={styles.upgradeButtonText}>
              {t("webPage.upgradeButton")}
            </Text>
          </Pressable>
        )}
        {screenHeader?.right?.menu && (
          <Menu
            actions={screenHeader.right.menu}
            actionComponent={
              <View
                style={{
                  height: ms(48),
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: ms(24),
                  borderRadius: ms(24),
                  backgroundColor: Colors.accent,
                  flexDirection: "row",
                  gap: ms(4),
                }}
              >
                <Text
                  style={{
                    color: Colors.white,
                    fontSize: ms(14),
                    fontWeight: "600",
                  }}
                >
                  {t("webPage.shareAndMore")}
                </Text>
                <Icon
                  icon="caretDownFilled"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.white,
                  }}
                />
              </View>
            }
          />
        )}
      </View>

      <View style={{ ...styles.mainContent, minHeight: height }}>
        {/* Sidebar */}
        <View style={{ ...styles.sidebar, position: "fixed" } as any}>
          <View style={styles.navigation}>
            <WebPage.NavItem
              title={t("webPage.home")}
              icon="home"
              onPress={_gotoHome}
              selected={tab === "home"}
            />
            <WebPage.NavItem
              title={t("webPage.settings")}
              icon="cog"
              onPress={_gotoSettings}
              selected={tab === "settings"}
            />
          </View>
        </View>

        {/* Main Content Area */}
        <View style={styles.contentArea}>{children}</View>
      </View>

      <WebPageFooter />
    </View>
  );
}

WebPage.NavItem = ({
  title,
  icon,
  onPress,
  selected,
}: {
  title: string;
  icon: IconType;
  onPress: () => void;
  selected: boolean;
}) => {
  return (
    <Pressable style={styles.navItem} onPress={onPress}>
      <Icon
        containerStyle={StyleSheet.flatten([
          styles.sideBarIconContainer,
          selected && {
            backgroundColor: Colors.text,
          },
        ])}
        icon={icon}
        style={StyleSheet.flatten([
          styles.sideBarIcon,
          selected && {
            tintColor: Colors.white,
          },
        ])}
      />
      <Text
        style={StyleSheet.flatten([
          styles.sideBarText,
          selected && {
            color: Colors.text,
          },
        ])}
      >
        {title}
      </Text>
    </Pressable>
  );
};

WebPage.ListMenu = ({
  items,
}: {
  items: { text: string; onPress: () => void }[];
}) => {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: ms(4),
        height: ms(48),
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Icon
              icon="caretRight"
              style={{
                width: ms(16),
                height: ms(16),
                tintColor: Colors.subtitle,
              }}
            />
          )}
          <Pressable onPress={item.onPress}>
            <Text
              style={{
                fontWeight: index === items.length - 1 ? "500" : "300",
                color:
                  index === items.length - 1 ? Colors.text : Colors.subtitle,
                fontSize: ms(14),
              }}
            >
              {item.text}
            </Text>
          </Pressable>
        </React.Fragment>
      ))}
    </View>
  );
};

WebPage.SIDEBAR_WIDTH = ms(200);
WebPage.HEADER_HEIGHT = ms(72);
WebPage.MAX_WIDTH = 1000;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: ms(20),
    flex: 1,
    flexDirection: "column",
    maxWidth: WebPage.MAX_WIDTH + ms(40),
    alignSelf: "center",
    width: "100%",
    marginHorizontal: "auto",
    paddingTop: ms(72),
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: Colors.text + "20",
    backgroundColor: Colors.bg,
    height: WebPage.HEADER_HEIGHT,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    maxWidth: WebPage.MAX_WIDTH,
    width: "100%",
    marginHorizontal: "auto",
  },
  logo: {
    fontSize: ms(20),
    fontWeight: "600",
    color: Colors.text,
  },
  logoAI: {
    color: Colors.subtitle,
    fontWeight: "400",
  },
  upgradeButton: {
    backgroundColor: "#8a15ff",
    paddingHorizontal: ms(16),
    paddingVertical: ms(10),
    borderRadius: ms(12),
    flexDirection: "row",
    alignItems: "center",
  },
  upgradeButtonText: {
    color: Colors.white,
    fontWeight: "600",
    fontSize: ms(14),
  },
  mainContent: {
    flex: 1,
    flexDirection: "row",
  },
  sidebar: {
    width: WebPage.SIDEBAR_WIDTH,
    paddingTop: ms(32),
    height: "100%",
    left: "50%",
    transform: "translateX(-500px)",
    top: WebPage.HEADER_HEIGHT,
  },
  navigation: {
    gap: ms(16),
  },
  navItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  contentArea: {
    flex: 1,
    paddingTop: ms(32),
    marginLeft: WebPage.SIDEBAR_WIDTH,
  },
  sideBarIconContainer: {
    width: ms(44),
    height: ms(44),
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e5e5ea",
    borderRadius: ms(80),
  },
  sideBarIcon: {
    tintColor: Colors.gray6,
    width: ms(20),
    height: ms(20),
  },
  sideBarText: {
    marginLeft: ms(12),
    fontSize: ms(16),
    fontWeight: "600",
    color: Colors.gray6,
  },
});
